import { Stack, Typography } from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";

import { getDollarValue } from "../../helpers/currency";
import { HubHookFormInput } from "../../components";

import ItemsTableRow from "./ItemsTableRow";
import {
  BasicCardStack,
  StyledHubHookFormDatePicker,
  StyledItemRow,
} from "./styles";

const totalReducer = (tol, curr) =>
  tol + (curr?.price || 0) * (curr?.quantity || 0);

export const PoDocItemsTable = ({ isMold = false, allowEdit = false }) => {
  const fieldArrayOptions = useFieldArray({
    name: "items",
  });
  const { append, remove, fields } = fieldArrayOptions;

  const { watch } = useFormContext();

  const items = watch("items") || [];
  const subtotal = items?.reduce(totalReducer, 0);
  const shippingCost = watch("shippingCost") || 0;
  const taxCost = watch("taxCost") || 0;
  const otherCost = watch("otherCost") || 0;

  const mathed = [subtotal, shippingCost, taxCost, otherCost]
    .map(Number)
    .reduce((tol, curr) => tol + curr, 0);
  const total = getDollarValue(mathed);

  const onAppend = (defaultValues = {}) => {
    append({
      price: Number(0.0).toFixed(2),
      quantity: 1,
      units: 1,
      ...defaultValues,
    });
  };

  const onRemove = (idx) => {
    remove(idx);
    if (items?.length - 1 === 0) onAppend({});
  };

  return (
    <BasicCardStack sx={{ padding: 0, gap: 0 }} justifyContent="space-between">
      <Stack>
        <StyledItemRow header includePadding>
          <Typography variant="caption">Line</Typography>
          <Typography variant="caption">Qty</Typography>
          <Typography variant="caption">Unit</Typography>
          <Typography variant="caption" className="ItemCell-root">
            Description
          </Typography>
          <Typography variant="caption" className="ItemCell-root">
            Unit Price
          </Typography>
          <Typography variant="caption" className="ItemCell-root">
            Amount
          </Typography>
        </StyledItemRow>
        {fields?.map((item, idx) => (
          <ItemsTableRow
            key={item?.id}
            item={item}
            idx={idx}
            isMold={isMold}
            onAppend={onAppend}
            onRemove={onRemove}
          />
        ))}

        <StyledItemRow rowHeight="24px">
          <Typography
            variant="caption"
            className="ItemCell-root"
            sx={{ gridColumn: "span 3", gridRow: "span 5" }}
          />
          <Stack gridRow="span 5" height="100%" overflow="hidden">
            <HubHookFormInput
              name="notes"
              placeholder="Notes..."
              shouldUnregister={false}
              formControlProps={{ sx: { textarea: { overflow: "hidden" } } }}
              disableHelperText
              multiline
              rows={5}
            />
          </Stack>
          <Typography variant="caption" className="ItemCell-root">
            Subtotal
          </Typography>
          <Typography variant="caption" className="ItemCell-root">
            {getDollarValue(subtotal)}
          </Typography>
          <Typography variant="caption" className="ItemCell-root">
            Shipping
          </Typography>
          <HubHookFormInput
            name="shippingCost"
            placeholder="0.00"
            shouldUnregister={false}
            disableHelperText
            isCurrency
          />
          <Typography variant="caption" className="ItemCell-root">
            Tax
          </Typography>
          <HubHookFormInput
            name="taxCost"
            placeholder="0.00"
            shouldUnregister={false}
            disableHelperText
            isCurrency
          />
          <Typography variant="caption" className="ItemCell-root">
            Other
          </Typography>
          <HubHookFormInput
            name="otherCost"
            placeholder="0.00"
            shouldUnregister={false}
            disableHelperText
            isCurrency
          />
          <Typography variant="boldCaption" className="ItemCell-root">
            Total
          </Typography>
          <Typography variant="caption" className="ItemCell-root">
            {total}
          </Typography>
        </StyledItemRow>
        <StyledItemRow>
          <Typography variant="caption" className="ItemCell-root" />
          <Stack
            direction="row"
            gap="5px"
            alignItems="center"
            justifyContent="start"
            gridColumn="span 3"
            py="10px"
          >
            <Typography variant="caption" sx={{ lineHeight: "12px" }}>
              Please notify us immediately if this order cannot be shipped
              complete on or before:
            </Typography>
          </Stack>
          <StyledHubHookFormDatePicker
            name="dueDate"
            placeholder="ASAP"
            shouldUnregister={false}
            disableHelperText
          />
        </StyledItemRow>
      </Stack>
    </BasicCardStack>
  );
};

export default PoDocItemsTable;
